// src/scripts/sqlApiHelper/stockUnitsApi.js

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "@/firebase";

import {
  fetchStockOptions,
  saveStockOptions,
} from "@/scripts/sqlApiHelper/stockOptionsApi";

/* =========================================================
   FIRESTORE LOCATIONS
   ========================================================= */

const STOCK_UNITS_COLLECTION =
  "stock_units";

const OPTIONS_COLLECTION =
  "stock_reference_data";

const OPTIONS_DOCUMENT =
  "dropdown_options";

const STOCK_OPTION_UPDATE_BATCH_SIZE =
  450;

/* =========================================================
   MANAGED STOCK OPTION FIELDS
   ========================================================= */

const MANAGED_OPTION_FIELDS = [
  "order_unit",
  "stock_unit",
  "supplier",
  "product_group",
];

/* =========================================================
   HELPERS
   ========================================================= */

function cleanString(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function optionKey(value) {
  return cleanString(
    value
  ).toLocaleLowerCase(
    "en-GB"
  );
}

function normaliseList(values) {
  const source =
    Array.isArray(values)
      ? values
      : [];

  const seen =
    new Set();

  const result = [];

  for (
    const value of source
  ) {
    const cleaned =
      cleanString(value);

    const key =
      optionKey(cleaned);

    if (
      !cleaned ||
      seen.has(key)
    ) {
      continue;
    }

    seen.add(key);
    result.push(cleaned);
  }

  return result;
}

function sortText(a, b) {
  return cleanString(
    a
  ).localeCompare(
    cleanString(b),
    "en-GB",
    {
      sensitivity: "base",
      numeric: true,
    }
  );
}

function cleanMoney(value) {
  const amount =
    Number(value);

  if (
    !Number.isFinite(amount) ||
    amount < 0
  ) {
    throw new Error(
      "Order Unit Cost must be a valid amount of £0.00 or more."
    );
  }

  return (
    Math.round(
      (amount + Number.EPSILON) *
        100
    ) / 100
  );
}

function cleanPositiveDecimal(
  value,
  label
) {
  const number =
    Number(value);

  if (
    !Number.isFinite(number) ||
    number <= 0
  ) {
    throw new Error(
      `${label} must be a number greater than zero.`
    );
  }

  return (
    Math.round(
      (number + Number.EPSILON) *
        10
    ) / 10
  );
}

function safePositiveNumber(
  value,
  fallback = 0
) {
  const number =
    Number(value);

  if (
    !Number.isFinite(number) ||
    number <= 0
  ) {
    return fallback;
  }

  return number;
}

function optionsRef() {
  return doc(
    db,
    OPTIONS_COLLECTION,
    OPTIONS_DOCUMENT
  );
}

function stockUnitsRef() {
  return collection(
    db,
    STOCK_UNITS_COLLECTION
  );
}

function findConfiguredValue(
  configuration,
  supplierName
) {
  if (
    !configuration ||
    typeof configuration !==
      "object" ||
    Array.isArray(configuration)
  ) {
    return undefined;
  }

  const requiredKey =
    optionKey(
      supplierName
    );

  const matchingEntry =
    Object.entries(
      configuration
    ).find(
      ([configuredName]) =>
        optionKey(
          configuredName
        ) === requiredKey
    );

  return matchingEntry?.[1];
}

function resolveManagedOption(
  value,
  managedValues
) {
  const cleaned =
    cleanString(value);

  if (!cleaned) {
    return "";
  }

  const existingValue =
    normaliseList(
      managedValues
    ).find(
      (managedValue) =>
        optionKey(
          managedValue
        ) ===
        optionKey(cleaned)
    );

  return (
    existingValue ||
    cleaned
  );
}

/* =========================================================
   LEGACY NORMALISATION

   No conversion is hardwired here.

   Old records did not distinguish:
   - order unit
   - stock unit
   - stock units per order unit
   - sales units per stock unit

   We expose temporary fallbacks to keep old records visible,
   but sales conversion is NOT guessed from the old stock_units
   field because that old field has historically been used for
   different purposes.

   Existing records should therefore be edited once in the new
   Stock Database.
   ========================================================= */

function normaliseStoredRecord(
  stockDocument
) {
  const raw =
    typeof stockDocument?.data ===
    "function"
      ? stockDocument.data()
      : stockDocument || {};

  const id =
    stockDocument?.id ||
    raw.id ||
    "";

  const legacyUnit =
    cleanString(
      raw.unit_size
    );

  const orderUnit =
    cleanString(
      raw.order_unit
    ) ||
    legacyUnit;

  const stockUnit =
    cleanString(
      raw.stock_unit
    ) ||
    legacyUnit ||
    orderUnit;

  const stockUnitsPerOrderUnit =
    safePositiveNumber(
      raw.stock_units_per_order_unit,
      safePositiveNumber(
        raw.stock_units,
        0
      )
    );

  const salesUnitsPerStockUnit =
    safePositiveNumber(
      raw.sales_units_per_stock_unit,
      safePositiveNumber(
        raw.sales_units,
        0
      )
    );

  return {
    ...raw,

    id,

    order_unit:
      orderUnit,

    stock_unit:
      stockUnit,

    stock_units_per_order_unit:
      stockUnitsPerOrderUnit,

    sales_units_per_stock_unit:
      salesUnitsPerStockUnit,

    /*
     * Compatibility aliases for existing CoreDeck components.
     * These aliases contain DATABASE values.
     */
    unit_size:
      stockUnit,

    stock_units:
      stockUnitsPerOrderUnit,

    sales_units:
      salesUnitsPerStockUnit,
  };
}

function sortStockUnits(items) {
  return [
    ...items,
  ].sort((a, b) => {
    const groupComparison =
      sortText(
        a.product_group,
        b.product_group
      );

    if (
      groupComparison !== 0
    ) {
      return groupComparison;
    }

    const itemComparison =
      sortText(
        a.item_name,
        b.item_name
      );

    if (
      itemComparison !== 0
    ) {
      return itemComparison;
    }

    return sortText(
      a.supplier,
      b.supplier
    );
  });
}

/* =========================================================
   STOCK OPTIONS RE-EXPORTS

   Retained so existing imports from stockUnitsApi continue
   working.
   ========================================================= */

export {
  fetchStockOptions,
  saveStockOptions,
};

/* =========================================================
   ACTIVE SUPPLIERS
   ========================================================= */

export async function fetchActiveSuppliers() {
  const stockSnapshot =
    await getDocs(
      stockUnitsRef()
    );

  const suppliersByKey =
    new Map();

  stockSnapshot.forEach(
    (stockDocument) => {
      const data =
        stockDocument.data();

      const supplierName =
        cleanString(
          data?.supplier
        );

      if (!supplierName) {
        return;
      }

      const key =
        optionKey(
          supplierName
        );

      if (
        !suppliersByKey.has(
          key
        )
      ) {
        suppliersByKey.set(
          key,
          supplierName
        );
      }
    }
  );

  let optionData = {};

  try {
    const optionSnapshot =
      await getDoc(
        optionsRef()
      );

    if (
      optionSnapshot.exists()
    ) {
      optionData =
        optionSnapshot.data();
    }
  } catch (error) {
    console.warn(
      "[stockUnitsApi] Supplier email configuration could not be loaded:",
      error
    );
  }

  const orderingEmails =
    optionData
      .supplier_ordering_emails &&
    typeof optionData
      .supplier_ordering_emails ===
      "object"
      ? optionData
          .supplier_ordering_emails
      : {};

  const supplierAliases =
    optionData
      .supplier_aliases &&
    typeof optionData
      .supplier_aliases ===
      "object"
      ? optionData
          .supplier_aliases
      : {};

  return Array.from(
    suppliersByKey.values()
  )
    .map(
      (supplierName) => {
        const configuredEmail =
          findConfiguredValue(
            orderingEmails,
            supplierName
          );

        const configuredAliases =
          findConfiguredValue(
            supplierAliases,
            supplierName
          );

        return {
          name:
            supplierName,

          email:
            cleanString(
              configuredEmail
            ),

          aliases:
            normaliseList(
              configuredAliases
            ),
        };
      }
    )
    .sort(
      (a, b) =>
        sortText(
          a.name,
          b.name
        )
    );
}

/* =========================================================
   STOCK UNIT NORMALISATION
   ========================================================= */

async function normaliseStockUnit(
  input = {}
) {
  const itemName =
    cleanString(
      input.item_name
    );

  if (!itemName) {
    throw new Error(
      "Item Name is required."
    );
  }

  const options =
    await fetchStockOptions();

  const orderUnit =
    resolveManagedOption(
      input.order_unit,
      options.orderUnits
    );

  const stockUnit =
    resolveManagedOption(
      input.stock_unit,
      options.stockUnits
    );

  const supplier =
    resolveManagedOption(
      input.supplier,
      options.suppliers
    );

  const productGroup =
    resolveManagedOption(
      input.product_group,
      options.productGroups
    );

  if (!orderUnit) {
    throw new Error(
      "Order Unit is required."
    );
  }

  if (!stockUnit) {
    throw new Error(
      "Stock Unit is required."
    );
  }

  if (!supplier) {
    throw new Error(
      "Supplier is required."
    );
  }

  if (!productGroup) {
    throw new Error(
      "Product Group is required."
    );
  }

  const stockUnitsPerOrderUnit =
    cleanPositiveDecimal(
      input.stock_units_per_order_unit,
      "Stock Units per Order Unit"
    );

  const salesUnitsPerStockUnit =
    cleanPositiveDecimal(
      input.sales_units_per_stock_unit,
      "Sales Units per Stock Unit"
    );

  const unitCost =
    cleanMoney(
      input.unit_cost
    );

  return {
    item_name:
      itemName,

    /*
     * Cost is the cost of ONE ORDER UNIT.
     */
    unit_cost:
      unitCost,

    order_unit:
      orderUnit,

    stock_unit:
      stockUnit,

    stock_units_per_order_unit:
      stockUnitsPerOrderUnit,

    sales_units_per_stock_unit:
      salesUnitsPerStockUnit,

    supplier,

    product_group:
      productGroup,

    /*
     * Compatibility fields.
     *
     * These allow existing pieces of CoreDeck which have not yet
     * been migrated to continue reading a sensible value.
     */
    unit_size:
      stockUnit,

    stock_units:
      stockUnitsPerOrderUnit,

    sales_units:
      salesUnitsPerStockUnit,
  };
}

/* =========================================================
   FETCH ALL STOCK UNITS
   ========================================================= */

export async function fetchStockUnits() {
  const snapshot =
    await getDocs(
      stockUnitsRef()
    );

  const items =
    snapshot.docs.map(
      (stockDocument) =>
        normaliseStoredRecord(
          stockDocument
        )
    );

  return sortStockUnits(
    items
  );
}

/* =========================================================
   FETCH STOCK UNITS FOR ONE SUPPLIER
   ========================================================= */

export async function fetchStockUnitsForSupplier(
  supplierName
) {
  const supplier =
    cleanString(
      supplierName
    );

  if (!supplier) {
    return [];
  }

  const supplierQuery =
    query(
      stockUnitsRef(),
      where(
        "supplier",
        "==",
        supplier
      )
    );

  const snapshot =
    await getDocs(
      supplierQuery
    );

  const items =
    snapshot.docs.map(
      (stockDocument) =>
        normaliseStoredRecord(
          stockDocument
        )
    );

  return sortStockUnits(
    items
  );
}

/* =========================================================
   CREATE STOCK UNIT
   ========================================================= */

export async function createStockUnit(
  input = {}
) {
  const payload =
    await normaliseStockUnit(
      input
    );

  const createdDocument =
    await addDoc(
      stockUnitsRef(),
      {
        ...payload,

        created_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),
      }
    );

  return createdDocument.id;
}

/* =========================================================
   UPDATE STOCK UNIT
   ========================================================= */

export async function updateStockUnit(
  stockUnitId,
  input = {}
) {
  const id =
    cleanString(
      stockUnitId
    );

  if (!id) {
    throw new Error(
      "A Stock Unit ID is required."
    );
  }

  const payload =
    await normaliseStockUnit(
      input
    );

  await updateDoc(
    doc(
      db,
      STOCK_UNITS_COLLECTION,
      id
    ),
    {
      ...payload,

      updated_at:
        serverTimestamp(),
    }
  );

  return id;
}

/* =========================================================
   DELETE STOCK UNIT
   ========================================================= */

export async function deleteStockUnit(
  stockUnitId
) {
  const id =
    cleanString(
      stockUnitId
    );

  if (!id) {
    throw new Error(
      "A Stock Unit ID is required."
    );
  }

  await deleteDoc(
    doc(
      db,
      STOCK_UNITS_COLLECTION,
      id
    )
  );
}

/* =========================================================
   REPLACE MANAGED OPTION VALUE
   ========================================================= */

export async function replaceStockUnitOptionValue(
  field,
  currentValue,
  replacementValue
) {
  const cleanField =
    cleanString(field);

  const from =
    cleanString(
      currentValue
    );

  const to =
    cleanString(
      replacementValue
    );

  if (
    !MANAGED_OPTION_FIELDS.includes(
      cleanField
    )
  ) {
    throw new Error(
      `Unsupported stock option field: ${cleanField}`
    );
  }

  if (!from) {
    throw new Error(
      "The current option value is required."
    );
  }

  if (!to) {
    throw new Error(
      "The replacement option value is required."
    );
  }

  if (
    optionKey(from) ===
    optionKey(to)
  ) {
    return 0;
  }

  const matchingQuery =
    query(
      stockUnitsRef(),
      where(
        cleanField,
        "==",
        from
      )
    );

  const snapshot =
    await getDocs(
      matchingQuery
    );

  if (snapshot.empty) {
    return 0;
  }

  const matchingDocuments =
    snapshot.docs;

  for (
    let start = 0;
    start <
    matchingDocuments.length;
    start +=
      STOCK_OPTION_UPDATE_BATCH_SIZE
  ) {
    const batch =
      writeBatch(db);

    const documentsForBatch =
      matchingDocuments.slice(
        start,
        start +
          STOCK_OPTION_UPDATE_BATCH_SIZE
      );

    for (
      const stockDocument of
      documentsForBatch
    ) {
      const update = {
        [cleanField]:
          to,

        updated_at:
          serverTimestamp(),
      };

      /*
       * Keep compatibility aliases in step with Stock Unit
       * renames.
       */
      if (
        cleanField ===
        "stock_unit"
      ) {
        update.unit_size =
          to;
      }

      batch.update(
        stockDocument.ref,
        update
      );
    }

    await batch.commit();
  }

  return snapshot.size;
}